"use client";

import type { SharedGeneration } from "@/app/_api/elevenlabs/sharedGenerations";

import { VideoPlayer } from "@ui/components/demos/ImageVideoHero/VideoPlayer";
import * as m from "motion/react-m";
import Image from "next/image";
import clsx from "clsx";
import { type Key, Tab, TabList, TabPanel, Tabs } from "react-aria-components";
import { useCallback, useState } from "react";
import { Noise } from "@ui/components/core/Noise/Noise";

export type MediaShowcaseItem = SharedGeneration & { title?: string };

interface HeroMediaShowcaseProps {
  items: MediaShowcaseItem[];
  modality: "image" | "video";
  modelName?: string;
  className?: string;
  enableOverlay?: boolean;
  enableNoise?: boolean;
}

export function HeroMediaShowcase({
  items,
  modality,
  modelName,
  className = "-tw-mx-px tw-rounded-3xl tw-bg-neutral-100 tw-h-[36rem]",
  enableOverlay = true,
  enableNoise = true,
}: HeroMediaShowcaseProps) {
  const [selectedKey, setSelectedKey] = useState<Key>(0);
  const activeIndex = Number(selectedKey);

  const handleSelectionChange = useCallback((key: Key) => {
    setSelectedKey(key);
  }, []);

  if (items.length === 0) return null;

  const active = items[activeIndex];
  const displayTitle = active.title ?? modelName;

  return (
    <div className='tw-relative tw-isolate tw-w-full'>
      <div
        className='tw-absolute -tw-z-10 tw-top-0 -tw-inset-x-px tw-h-8'
        style={{ backgroundColor: "var(--marker-background-color)" }}
      />
      <Tabs selectedKey={selectedKey} onSelectionChange={handleSelectionChange} className='tw-w-full'>
        <div className={clsx("tw-relative tw-w-full tw-overflow-hidden", className)}>
          {/* All panels force-mounted for crossfade without unmount/remount */}
          <div className='tw-absolute tw-inset-0' style={{ isolation: "isolate" }}>
            {items.map((gen, i) => (
              <TabPanel key={gen.id} id={i} shouldForceMount className='tw-absolute tw-inset-0 tw-outline-none'>
                {({ isInert }) => (
                  <m.div
                    className='tw-absolute tw-inset-0'
                    animate={{ opacity: isInert ? 0 : 1 }}
                    initial={false}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    {modality === "video" ? (
                      <VideoPlayer
                        src={gen.content_url}
                        shouldPlay={!isInert}
                        autoPlay={i === 0}
                        loop
                        muted
                        hasAudio={false}
                        rounded={false}
                      />
                    ) : (
                      <Image
                        src={gen.content_url}
                        alt={gen.description}
                        fill
                        className='tw-object-cover'
                        sizes='100vw'
                        placeholder={gen.blurDataURL ? "blur" : "empty"}
                        blurDataURL={gen.blurDataURL}
                        loading='eager'
                      />
                    )}
                    {enableNoise && <Noise className='tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-60' />}
                  </m.div>
                )}
              </TabPanel>
            ))}
          </div>

          {enableOverlay && (
            <>
              {/* Gradient scrim for text readability */}
              <div className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[2] tw-bg-gradient-to-b tw-from-black/50 tw-via-transparent tw-to-black/30' />

              {/* Text overlay — top left */}
              <div className='tw-absolute tw-top-0 tw-left-0 tw-z-10 tw-p-6 md:tw-p-8 lg:tw-p-10'>
                {displayTitle && (
                  <h2 className='tw-text-white tw-pb-1 tw-type-base sm:tw-type-lg md:tw-type-xl lg:tw-type-2xl'>
                    {displayTitle}
                  </h2>
                )}
                {active.description && (
                  <p className='tw-py-1 tw-mt-1 tw-max-w-xs sm:tw-max-w-sm md:tw-max-w-md tw-text-white/60 tw-line-clamp-2 sm:tw-line-clamp-3 tw-type-xs sm:tw-type-sm'>
                    {active.description}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Thumbnail tabs — bottom center */}
          {items.length > 1 && (
            <TabList className='tw-absolute tw-bottom-4 tw-left-1/2 tw-z-10 tw--translate-x-1/2 tw-flex tw-gap-2 md:tw-bottom-6 md:tw-gap-3'>
              {items.map((gen, i) => (
                <Tab
                  key={gen.id}
                  id={i}
                  className={clsx(
                    "tw-relative tw-h-14 tw-w-20 tw-rounded-xl tw-p-0.5 tw-transition-all tw-cursor-pointer tw-outline-none focus-visible:tw-outline-focus",
                    i === activeIndex ? "tw-ring-1 tw-ring-white" : "tw-ring-0 hover:tw-ring-1 hover:tw-ring-white/60",
                  )}
                >
                  <div className='tw-relative tw-w-full tw-h-full tw-overflow-hidden tw-rounded-xl'>
                    <Image
                      src={gen.thumbnail_url}
                      alt={gen.description}
                      fill
                      className='tw-object-cover'
                      sizes='80px'
                      placeholder={gen.blurDataURL ? "blur" : "empty"}
                      blurDataURL={gen.blurDataURL}
                    />
                    {enableNoise && (
                      <Noise className='tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[1] tw-opacity-60' />
                    )}
                  </div>
                </Tab>
              ))}
            </TabList>
          )}
        </div>
      </Tabs>
    </div>
  );
}
